import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import { Subscription, timer } from 'rxjs';
import { filter } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { AuthenticationService } from 'app/_services/authentication.service';
import { SandboxCompanyStatus, SandboxService, SandboxStatus } from 'app/_services/sandbox.service';
import { ConfirmationModalComponent } from 'app/_components/confirmation-modal/confirmation-modal.component';

const TITLE_PREFIX = '[SANDBOX] ';
/** How often to ask the API while a refresh is running / when idle. */
const POLL_BUSY_MS = 10 * 1000;
const POLL_IDLE_MS = 5 * 60 * 1000;
/** Staff signed in but the header has not picked a company yet. */
const POLL_WAIT_COMPANY_MS = 3 * 1000;
/** After a client's own refresh finishes, reload so nothing stale is left. */
const RELOAD_AFTER_DONE_MS = 2500;

/**
 * Permanent "you are in the sandbox" strip. Rendered on every page (site
 * layout and login layout) and shows nothing outside the sandbox.
 *
 * Phase 2 (spec 17.4): when someone is signed in, the strip also shows how
 * old the sandbox data is and carries the one action, "Refresh from live",
 * backed by GET/POST /api/sandbox/status|refresh. While the signed-in
 * client's own company is mid-refresh the strip shows progress and tells
 * the site layout to hold the page (SandboxService.holding$).
 *
 * Who the strip is about:
 *   client / grant applicant  -> their own company (the API decides)
 *   staff / agency staff      -> the company currently selected in the header
 *   contact                   -> nobody; just the shared data age
 * Who may press the button: Barkani staff, agency staff (their companies),
 * and corporate-level client users. Same rule as the API.
 */
@Component({
  selector: 'app-sandbox-banner',
  templateUrl: './sandbox-banner.component.html',
  styleUrls: ['./sandbox-banner.component.scss'],
})
export class SandboxBannerComponent implements OnInit, OnDestroy {
  /** Slimmer strip for the login pages (no API calls there: nobody is signed in). */
  @Input() compact = false;

  readonly isSandbox: boolean = !!environment.isSandbox;
  /** The matching live site, for the "Back to live" link. Empty = hidden. */
  readonly liveUrl: string = environment.liveUrl || '';

  status: SandboxStatus | null = null;
  company: SandboxCompanyStatus | null = null;
  /** The API could not be reached; keep the static strip, hide the details. */
  loadFailed = false;
  starting = false;

  private pollSub: Subscription | null = null;
  private navSub: Subscription | null = null;
  private companySub: Subscription | null = null;
  private loadSub: Subscription | null = null;
  private shownCompanyId: string | null = null;
  private wasInProgress = false;

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private sandboxService: SandboxService,
    private modalService: BsModalService,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {
    if (!this.isSandbox) return;
    if (typeof document !== 'undefined' && !document.title.startsWith(TITLE_PREFIX)) {
      document.title = TITLE_PREFIX + document.title;
    }
    if (!this.live) return;

    this.load();
    // Staff switch companies in the header without this component being
    // re-created; follow the selection. On a fresh sign-in the current
    // company is only known once the site layout has loaded the company
    // list, which is after this init, so listen for that too.
    this.navSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.reloadIfCompanyChanged());
    this.companySub = this.authenticationService.currentCompanyChanged$
      .subscribe(() => this.reloadIfCompanyChanged());
  }

  ngOnDestroy() {
    this.stopPolling();
    if (this.navSub) this.navSub.unsubscribe();
    if (this.companySub) this.companySub.unsubscribe();
    if (this.loadSub) this.loadSub.unsubscribe();
    this.sandboxService.setHolding(false);
  }

  /* ------------------------------ what to show ----------------------------- */

  /** Signed in on the full layout: the strip talks to the API. */
  get live(): boolean {
    return this.isSandbox && !this.compact && this.authenticationService.isSessionValid();
  }

  /** The signed-in user is a client, so `company` is always their own. */
  get isOwnCompany(): boolean {
    return this.authenticationService.is_client();
  }

  /** Same rule as POST /api/sandbox/refresh. */
  get mayRefresh(): boolean {
    if (!this.company || !this.company.enabled) return false;
    const auth = this.authenticationService;
    if (auth.is_staff() || auth.is_agency_staff()) return true;
    try {
      return auth.is_client_corporate();
    } catch (e) {
      return false; // client with no level record
    }
  }

  get inProgress(): boolean {
    return !!(this.company && this.company.in_progress);
  }

  get dataAge(): string {
    if (!this.company || !this.company.last_refreshed_at) return '';
    return moment(this.company.last_refreshed_at).fromNow();
  }

  get dataAgeExact(): string {
    const at = this.company ? this.company.last_refreshed_at : null;
    return at ? moment(at).format('MMM D, YYYY h:mm A') : '';
  }

  get sharedDataAge(): string {
    const at = this.status ? this.status.common_last_refreshed_at : null;
    return at ? moment(at).fromNow() : '';
  }

  get progressSince(): string {
    const r = this.company && this.company.refresh;
    return r && r.started_at ? moment(r.started_at).fromNow() : '';
  }

  get lastError(): string {
    const r = this.company && this.company.refresh;
    return r && r.state === 'failed' && r.error ? r.error : '';
  }

  /** Why the button is disabled, for the tooltip. */
  get blockedReason(): string {
    if (!this.company || this.company.can_refresh) return '';
    if (this.company.next_allowed_at && !this.inProgress) {
      const hours = this.status ? this.status.refresh_interval_hours : 4;
      return `Allowed once every ${hours} hours. Next: ${moment(this.company.next_allowed_at).fromNow()}.`;
    }
    return this.company.blocked_reason || '';
  }

  /* -------------------------------- actions -------------------------------- */

  confirmRefresh(): void {
    if (!this.company || !this.mayRefresh || !this.company.can_refresh || this.starting) return;
    const name = this.company.company_name;
    const modal = this.modalService.show(ConfirmationModalComponent, { class: 'modal-md' });
    const content = modal.content as ConfirmationModalComponent;
    content.showConfirmationModal(
      'Refresh the sandbox from live?',
      `<p>This <strong>deletes everything in the sandbox for ${escapeHtml(name)}</strong>`
      + ' (events, settings, test data you created) and replaces it with a fresh copy of the live data.</p>'
      + '<p><strong>It cannot be undone.</strong> The sandbox for this company is unavailable while it runs,'
      + ' usually a few minutes.</p>'
      + '<p class="mb-0">Your live data is never changed.</p>',
      'Cancel',
      'Delete sandbox data and refresh',
    );
    content.onClose.subscribe((result) => {
      if (result === true) this.startRefresh();
    });
  }

  private startRefresh(): void {
    if (!this.company) return;
    this.starting = true;
    const companyId = this.isOwnCompany ? null : this.company.company_id;
    this.sandboxService.refresh(companyId).subscribe({
      next: (res) => {
        this.starting = false;
        this.toastr.info(res.message || 'Refresh started.');
        this.load();
      },
      error: (err) => {
        this.starting = false;
        const message = (err && err.error && err.error.error && err.error.error.message)
          || 'Could not start the refresh. Please try again.';
        this.toastr.error(message);
        this.load(); // the lock / rate limit may have changed
      },
    });
  }

  /* -------------------------------- loading -------------------------------- */

  private targetCompanyId(): string | null {
    const auth = this.authenticationService;
    if (auth.is_staff() || auth.is_agency_staff()) return auth.current_company_id() || null;
    return null; // clients: the API picks their own; contacts: nobody
  }

  private reloadIfCompanyChanged(): void {
    if (this.targetCompanyId() !== this.shownCompanyId) this.load();
  }

  /** Staff / agency staff with no company picked yet: nothing to show. */
  private get waitingForCompany(): boolean {
    const auth = this.authenticationService;
    return (auth.is_staff() || auth.is_agency_staff()) && !auth.current_company_id();
  }

  private load(): void {
    if (!this.live) return;
    const companyId = this.targetCompanyId();
    if (this.loadSub) this.loadSub.unsubscribe();
    this.loadSub = this.sandboxService.status(companyId).subscribe({
      next: (status) => {
        this.shownCompanyId = companyId;
        this.loadFailed = false;
        this.apply(status);
        if (this.waitingForCompany) {
          this.schedule(POLL_WAIT_COMPANY_MS); // the header will pick one shortly
        } else {
          this.schedule(this.inProgress ? POLL_BUSY_MS : POLL_IDLE_MS);
        }
      },
      error: () => {
        this.shownCompanyId = companyId;
        this.loadFailed = true;
        this.schedule(POLL_BUSY_MS * 3);
      },
    });
  }

  private apply(status: SandboxStatus): void {
    this.status = status;
    this.company = status.company;
    const nowInProgress = this.inProgress;

    if (this.wasInProgress && !nowInProgress && this.company) {
      if (this.company.state === 'done') {
        this.toastr.success(`Sandbox data for ${this.company.company_name} was refreshed from live.`);
        if (this.isOwnCompany) {
          // The company was inactive for a while; everything cached in the
          // app (company list, bureaus, open pages) is stale. Start clean.
          timer(RELOAD_AFTER_DONE_MS).subscribe(() => window.location.reload());
        }
      } else if (this.company.state === 'failed') {
        this.toastr.error(`The sandbox refresh for ${this.company.company_name} failed. Please contact Barkani.`);
      }
    }
    this.wasInProgress = nowInProgress;
    this.sandboxService.setHolding(this.isOwnCompany && nowInProgress);
  }

  private schedule(ms: number): void {
    this.stopPolling();
    this.pollSub = timer(ms).subscribe(() => this.load());
  }

  private stopPolling(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
      this.pollSub = null;
    }
  }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;',
  }[c]));
}
